/**
 * The one name a session has wherever it is listed (object checklist item 6:
 * the user's words; sprint 1 cleanup, round one).
 *
 * A session is named from what is stored on it, never from its place in a
 * list: `Session 9 · Ethics of speech` with a number and a label, `Session 9`
 * with a number alone, the label alone with no number, and its day alone
 * (`Tue, Oct 13`, in the course zone; an untimed session's stored date) with
 * neither. Under a day head (Home's week register, All sessions, a row's day
 * column) the day is already said, so `underDay` names a session with neither
 * as `Session` and leaves the rest to the row's time column; a caller passes it
 * only where the row says the day. A session whose day is not known either is
 * an `Unscheduled session`, never a bare `Session`.
 *
 * Generated sessions store their own date as the label (`Nov 2`, the
 * generator's `Mon FMDD`). That restates the day every caller already says, so
 * such a label is no name: `Session 6`, never `Session 6 · Nov 2, Mon, Nov 2`.
 */
import type { Session } from "@/lib/api/attendance";
import { formatInCourseZone } from "@/lib/session-portal";

export type ClassIdentity = Pick<Session, "session_no" | "label" | "starts_at"> & {
  /** The stored date; null where the session is known from a record that does not carry it. */
  session_date: string | null;
};

const DAY = { weekday: "short", month: "short", day: "numeric" } as const;
const STORED_DAY = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });

/** The label, unless it only restates the session's stored date. */
function nameLabel(session: ClassIdentity): string | undefined {
  const label = session.label?.trim();
  if (!label || !session.session_date) return label || undefined;
  return label === STORED_DAY.format(new Date(`${session.session_date}T12:00:00Z`)) ? undefined : label;
}

export function classTitle(session: ClassIdentity, timezone: string, { underDay = false } = {}): string {
  const label = nameLabel(session);
  if (session.session_no != null) return label ? `Session ${session.session_no} · ${label}` : `Session ${session.session_no}`;
  if (label) return label;
  const day = session.starts_at ? formatInCourseZone(session.starts_at, timezone, DAY)
    : session.session_date ? formatInCourseZone(`${session.session_date}T00:00:00Z`, "UTC", DAY)
      : null;
  if (day === null) return "Unscheduled session";
  return underDay ? "Session" : day;
}
